import { useApplyPanDelta } from './useApplyPanDelta.js';
import { useApplyZoomDelta } from './useApplyZoomDelta.js';
import { useResetInteractions } from './useResetInteractions.js';
import { useZoomToSelection } from './useZoomToSelection.js';
import {
  INTERACTION_MODE,
  type InteractionMode,
  type InteractionTriggers,
  isSelectCancelled,
  type ZoomBehavior,
} from '../../../../core/components/interactions-overlay/types.js';
import {
  reduceExploreCancelAll,
  reduceExploreCancelSelect,
  reduceExploreEnd,
  reduceExplorePin,
  reduceExplorePosition,
  reduceExploreReset,
  reduceExploreSelect,
  reduceExploreUnPin,
} from '../../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { reduceInteractionMode } from '../../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { reduceToolbarVisible } from '../../../../core/components/interactions-state-helpers/reducers/toolbar-reducers.js';
import { useSynchronizedCrosshair } from '../../../../core/components/shared-chart-interactions/hooks/useSynchronizedCrosshair.js';
import type { RelativeAbsolutePosition } from '../../../../core/types/pointer-events.js';
import { ZOOM_CHANGE_TYPE } from '../../../../core/types/zoom.js';
import { useDotSeriesData } from '../../../hooks/useDotSeriesData.js';
import { useRectData } from '../../../hooks/useRectData.js';
import { useVisibleXAxisByPriority } from '../../../hooks/useVisibleXAxisByPriority.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartId } from '../../../hooks/useXYChartId.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import {
  reduceDotTooltipContent,
  reduceRectTooltipContent,
  reduceUndefinedTooltipContent,
} from '../../../store/reducer-tooltip.js';
import {
  useInteractionModeState,
  useSetState,
  useZoomState,
} from '../../../store/selectors.js';
import { isTimeScale } from '../../../types/guards.js';
import { getHoveredDotDatapoint } from '../utils/dot-series-tooltip.js';
import { getHoveredRectDatapoint } from '../utils/rect-tooltip.js';

export const useOverlayEvents = () => {
  const { seriesWithDatapoints: rectData } = useRectData();
  const { seriesWithDatapoints: dotSeriesData } = useDotSeriesData();
  const axisIdToScales = useXYChartScales();
  const setState = useSetState();
  const mode = useInteractionModeState();
  const applyPanDelta = useApplyPanDelta();
  const applyReset = useResetInteractions();
  const applyZoomDelta = useApplyZoomDelta();
  const zoomToSelection = useZoomToSelection();
  const zoomState = useZoomState();
  const { axesMetadata, datapointMetadataMap } = useXYChartData();
  const chartId = useXYChartId();
  const synchronizedCrosshair = useSynchronizedCrosshair();
  const syncXAxes = useVisibleXAxisByPriority();

  const {
    updateHoverState: updateSynchronizedCrosshair,
    setDisabled: disableSynchronizedCrosshair,
  } = synchronizedCrosshair ?? {};

  const handleModeChange = (event: {
    mode: InteractionMode;
    keyboard: boolean;
  }) => {
    const canPan = axesMetadata.isPanEnabled && zoomState.zoomOutEnabled;
    const shouldSwitchToPanMode = event.mode === 'pan' && canPan;
    const shouldSwitchToZoomMode =
      event.mode === 'zoom' && axesMetadata.isZoomEnabled;

    if (shouldSwitchToPanMode || shouldSwitchToZoomMode) {
      setState(reduceInteractionMode(event.mode));
      setState(reduceExploreReset);
    } else if (event.mode === 'explore') {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    }
  };

  const handleHover = ({
    triggeredBy,
    position,
  }: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => {
    if (mode === INTERACTION_MODE.Explore) {
      setState(reduceExplorePosition(position));
    }
    setState(reduceToolbarVisible(true));

    if (isTimeScale(syncXAxes)) {
      const hoveredDate = syncXAxes.axisScale.invert(position.relative.x);
      updateSynchronizedCrosshair?.(chartId, hoveredDate.valueOf());
    }

    const { datapoint: dotDatapoint, series: dotSeries } =
      getHoveredDotDatapoint(
        position.relative,
        dotSeriesData,
        axisIdToScales,
      ) ?? {};

    if (dotDatapoint && dotSeries) {
      setState(reduceDotTooltipContent(dotDatapoint, dotSeries));

      return;
    }

    const { datapoint: rectDatapoint, series: rectSeries } =
      getHoveredRectDatapoint(
        position.relative,
        rectData,
        axisIdToScales,
        datapointMetadataMap,
      ) ?? {};

    if (rectDatapoint && rectSeries) {
      setState(reduceRectTooltipContent(rectDatapoint, rectSeries));

      return;
    }

    setState(reduceUndefinedTooltipContent);
  };

  const handleTogglePin = (
    pin: boolean,
    position: RelativeAbsolutePosition | undefined,
  ) => {
    if (pin) {
      handlePin(position);
      updateSynchronizedCrosshair?.(undefined);
    } else {
      handleUnPin(position);
    }
  };

  const handleUnPin = (position: RelativeAbsolutePosition | undefined) => {
    setState(reduceExploreUnPin(position));

    if (position) {
      const { datapoint: dotDatapoint, series: dotSeries } =
        getHoveredDotDatapoint(
          position.relative,
          dotSeriesData,
          axisIdToScales,
        ) ?? {};

      if (dotDatapoint && dotSeries) {
        setState(reduceDotTooltipContent(dotDatapoint, dotSeries));

        return;
      }

      const { datapoint: rectDatapoint, series: rectSeries } =
        getHoveredRectDatapoint(
          position.relative,
          rectData,
          axisIdToScales,
          datapointMetadataMap,
        ) ?? {};

      if (rectDatapoint && rectSeries) {
        setState(reduceRectTooltipContent(rectDatapoint, rectSeries));

        return;
      }
    }

    setState(reduceUndefinedTooltipContent);
  };

  const handlePin = (position: RelativeAbsolutePosition | undefined) => {
    if (!position) {
      return;
    }

    const { datapoint: dotDatapoint, series: dotSeries } =
      getHoveredDotDatapoint(
        position.relative,
        dotSeriesData,
        axisIdToScales,
      ) ?? {};

    if (dotDatapoint && dotSeries) {
      setState(reduceDotTooltipContent(dotDatapoint, dotSeries));
      setState(reduceExplorePin(position, [dotSeries.id]));

      return;
    }

    const { datapoint: rectDatapoint, series: rectSeries } =
      getHoveredRectDatapoint(
        position.relative,
        rectData,
        axisIdToScales,
        datapointMetadataMap,
      ) ?? {};

    if (rectDatapoint && rectSeries) {
      setState(reduceRectTooltipContent(rectDatapoint, rectSeries));
      setState(reduceExplorePin(position));

      return;
    }

    setState(reduceUndefinedTooltipContent);
  };

  const handleReset = (event: {
    triggeredBy: InteractionTriggers;
    mousePosition?: RelativeAbsolutePosition;
  }) => {
    const { mousePosition: position } = event;
    applyReset({ position });
    disableSynchronizedCrosshair?.(false);

    if (!position) {
      setState(reduceUndefinedTooltipContent);
      setState(reduceExploreCancelAll);

      return;
    }

    setState(reduceExploreCancelAll);
    setState(reduceExplorePosition(position));
    setState(reduceToolbarVisible(true));

    const { datapoint: dotDatapoint, series: dotSeries } =
      getHoveredDotDatapoint(
        position.relative,
        dotSeriesData,
        axisIdToScales,
      ) ?? {};

    if (dotDatapoint && dotSeries) {
      setState(reduceDotTooltipContent(dotDatapoint, dotSeries));

      return;
    }

    const { datapoint: rectDatapoint, series: rectSeries } =
      getHoveredRectDatapoint(
        position.relative,
        rectData,
        axisIdToScales,
        datapointMetadataMap,
      ) ?? {};

    if (rectDatapoint && rectSeries) {
      setState(reduceRectTooltipContent(rectDatapoint, rectSeries));

      return;
    }

    setState(reduceUndefinedTooltipContent);
  };

  const handleLeave = (_: { triggeredBy: InteractionTriggers }) => {
    setState(reduceExploreEnd);
    setState(reduceToolbarVisible(false));
    updateSynchronizedCrosshair?.(undefined);
  };

  const handlePanChange = (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => {
    applyPanDelta(event.delta);
  };

  const handleFocus = () => {
    setState(reduceToolbarVisible(true));
    disableSynchronizedCrosshair?.(false);
  };

  const handleSelectChange = (event: {
    triggeredBy: InteractionTriggers;
    start: RelativeAbsolutePosition;
    end: RelativeAbsolutePosition;
    mode: 'zoom' | 'explore';
  }): void => {
    setState(
      reduceExploreSelect({
        start: event.start,
        end: event.end,
        finished: false,
        accessible: false,
        // TODO APPDEV-10454 consider change variant when implementing select
        variant: 'zoom',
      }),
    );
  };

  const handleSelectEnd = (
    event:
      | {
          triggeredBy: InteractionTriggers;
          start: RelativeAbsolutePosition;
          end: RelativeAbsolutePosition;
        }
      | { cancelled: true },
  ): void => {
    if (isSelectCancelled(event)) {
      setState(reduceExploreCancelSelect);
    } else {
      // in case select is disabled and zoomX is enabled, only zoom to selection interaction is possible
      zoomToSelection();
    }
  };

  const handleZoomChange = (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => {
    const canZoomIn =
      event.behavior === ZOOM_CHANGE_TYPE.ZoomIn && zoomState?.zoomInEnabled;
    const canZoomOut =
      event.behavior === ZOOM_CHANGE_TYPE.ZoomOut && zoomState.zoomOutEnabled;
    const canZoomX = event.behavior === ZOOM_CHANGE_TYPE.ZoomX;

    const canZoom = canZoomIn || canZoomOut || canZoomX;

    if (canZoom && axesMetadata.isZoomEnabled) {
      setState(reduceExploreCancelAll);

      applyZoomDelta(event.deltaStart, event.deltaEnd, event.behavior);
    }
  };

  return {
    handleModeChange,
    handleHover,
    handleTogglePin,
    handleReset,
    handleLeave,
    handlePanChange,
    handleFocus,
    handleSelectChange,
    handleSelectEnd,
    handleZoomChange,
  };
};
